<template>
    <div v-if="project">
        <!-- Breadcrumb Area -->
        <div class="breadcrumb-area project-bg">
            <div class="overlay"></div>
            <div class="container">
                <div class="row">
                    <div class="col-lg-8">
                        <div class="breadcrumb-title">
                            <h1>{{ project.title }}</h1>
                            <p>{{ project.project_intro_desc }}</p>
                        </div>
                    </div>
                </div>
                <!-- Project Info Box -->
                <div class="project-info-box mt-4">
                    <h4>Project Info</h4>
                    <ul class="info-box-list">
                        <li>
                            <strong>Project:</strong>
                            <span>{{ project.project_info_title }}</span>
                        </li>
                        <li>
                            <strong>Category:</strong>
                            <span>{{ project.project_category }}</span>
                        </li>
                        <li>
                            <strong>Date:</strong>
                            <span>{{ project.project_date }}</span>
                        </li>
                        <li>
                            <strong>Client:</strong>
                            <span>{{ project.project_client }}</span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>

        <div class="breadcrumb-meta">
            <div class="container">
                <ul class="breadcrumb d-flex">
                    <li class="breadcrumb-item"><router-link to="/">Home</router-link></li>
                    <li class="breadcrumb-item"><router-link to="/projects">Projects</router-link></li>
                    <li class="breadcrumb-item active" aria-current="page">{{ project.title }}</li>
                </ul>
            </div>
        </div>

        <!-- Project Details -->
        <div id="project-page">
            <div class="project-details-section section-padding">
                <div class="container">
                    <div class="row">
                        <div class="col-xl-8 col-lg-8">
                            <!-- Main Content -->
                            <div class="project-content">
                                <div class="project-images">
                                    <div class="row">
                                        <div class="col-md-12 mb-4" v-if="project.images && project.images.image1">
                                            <div class="main-image">
                                                <img :src="project.images.image1" :alt="project.title"
                                                    class="img-fluid">
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="project-description mt-4">
                                    <h3>{{ project.project_subtitle_one }}</h3>
                                    <p>{{ project.project_description_one }}</p>
                                </div>

                                <div class="sensor-selection mt-5"
                                    v-if="Object.keys(project.thingspeak_channels).length > 0">
                                    <h3>Sensor Data</h3>
                                    <div class="form-group mb-4"
                                        v-if="Object.keys(project.thingspeak_channels).length > 1">
                                        <label for="sensor-select"><strong>Selected Sensor:</strong></label>
                                        <select id="sensor-select" v-model="selectedSensor" @change="updateCharts"
                                            class="form-control">
                                            <option v-for="(channelId, sensorName) in project.thingspeak_channels"
                                                :key="sensorName" :value="sensorName">
                                                {{ sensorName }}
                                            </option>
                                        </select>
                                    </div>

                                    <div class="days-selection mb-4">
                                        <div class="form-group">
                                            <label for="days-input"><strong>Days to display:</strong></label>
                                            <div class="input-group">
                                                <input type="number" id="days-input" v-model.number="days" min="1"
                                                    max="60" class="form-control">
                                                <div class="input-group-append">
                                                    <button class="btn btn-primary" @click="updateCharts">Update
                                                        Charts</button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Sensor Parameter Charts -->
                                    <div class="sensor-charts">
                                        <div v-for="param in project.sensor_parameters" :key="param.name"
                                            class="chart-container mb-5">
                                            <h4>{{ param.name }} ({{ param.unit }})</h4>
                                            <div class="thingspeak-chart">
                                                <iframe :src="getChartUrl(selectedChannelId, param.field)" width="100%"
                                                    height="260" frameborder="0">
                                                </iframe>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="additional-details mt-5">
                                    <h3>{{ project.project_subtitle_two }}</h3>
                                    <p>{{ project.project_description_two }}</p>
                                </div>

                                <!-- System Architecture -->
                                <div class="system-architecture mt-5" v-if="project.images && project.images.image2">
                                    <h3>System Architecture</h3>
                                    <p>The challenge with cellular-based systems is the need for a custom data
                                        processing backend.
                                        A flow chart of our process is shown below.</p>
                                    <div class="architecture-image mt-3">
                                        <img :src="project.images.image2" alt="System Architecture" class="img-fluid">
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="col-xl-4 col-lg-4">
                            <!-- Sidebar -->
                            <div class="project-sidebar">
                                <!-- Sensor Location -->
                                <div class="project-location-box mt-4">
                                    <h4>Sensor Location</h4>
                                    <div class="map-container">
                                        <div id="project-map" style="height: 300px;"></div>
                                    </div>
                                    <p class="location-address mt-2">
                                        <i class="fas fa-map-marker-alt"></i> {{ project.location.address }}
                                    </p>
                                </div>

                                <!-- Sensor Vitals -->
                                <div class="sensor-vitals-box mt-4">
                                    <h4>Sensor Vitals</h4>
                                    <div class="sensor-status">
                                        <div class="status-indicator"
                                            :class="{ 'online': isOnline, 'offline': !isOnline }">
                                            <span class="status-dot"></span>
                                            <span class="status-text">{{ isOnline ? 'Online' : 'Offline' }}</span>
                                        </div>
                                        <div class="last-ping mt-2" v-if="lastPing">
                                            <small>Last Ping: {{ lastPing }}</small>
                                        </div>
                                    </div>
                                    <ul class="sensor-vitals-list mt-3">
                                        <li>
                                            <strong>Battery:</strong>
                                            <span class="sensor-value">{{ project.sensor_vitals.battery_percentage
                                            }}</span>
                                        </li>
                                        <li>
                                            <strong>Charge State:</strong>
                                            <span class="sensor-value">{{ project.sensor_vitals.charge_state
                                            }}</span>
                                        </li>
                                    </ul>
                                </div>
                                <!-- Back to Projects -->
                                <div class="back-to-projects mt-4">
                                    <router-link to="/projects" class="btn btn-outline-primary btn-block">
                                        <i class="fas fa-arrow-left"></i> Back to All Projects
                                    </router-link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div v-else class="loading-container">
        <div class="container">
            <div class="text-center py-5">
                <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
                <p class="mt-3">Loading project details...</p>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Icon } from 'leaflet';

// Fix Leaflet's default icon path issues
delete Icon.Default.prototype._getIconUrl;
Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png')
});

export default {
    name: "ProjectDetails",
    data() {
        return {
            project: null,
            selectedSensor: null,
            days: 5,
            map: null,
            isOnline: false,
            lastPing: null,
            statusCheckInterval: null
        };
    },
    computed: {
        selectedChannelId() {
            if (!this.project || !this.selectedSensor) return null;
            return this.project.thingspeak_channels[this.selectedSensor];
        }
    },
    async mounted() {
        window.scrollTo(0, 0);
        try {
            // Fetch project data
            const response = await axios.get("/assets/data/single_projects.json");
            const projectId = this.$route.params.id;
            this.project = response.data.projects.find(p => p.id === projectId);

            if (!this.project) {
                this.$router.push('/projects');
                return;
            }

            // Set default selected sensor
            if (this.project.thingspeak_channels && Object.keys(this.project.thingspeak_channels).length > 0) {
                this.selectedSensor = Object.keys(this.project.thingspeak_channels)[0];
            }

            // Initialize map after project is loaded
            this.$nextTick(() => {
                this.initMap();
                this.checkSensorStatus();

                // Set up interval to check sensor status
                this.statusCheckInterval = setInterval(this.checkSensorStatus, 30000); // Check every 30 seconds
            });
        } catch (error) {
            console.error("Error loading project data:", error);
        }
    },
    beforeUnmount() {
        if (this.map) {
            this.map.remove();
        }

        if (this.statusCheckInterval) {
            clearInterval(this.statusCheckInterval);
        }
    },
    methods: {
        initMap() {
            if (!this.project || !this.project.location) return;

            const { latitude, longitude } = this.project.location;

            // Initialize map
            this.map = L.map('project-map').setView([latitude, longitude], 15);

            // Add OpenStreetMap tiles
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            }).addTo(this.map);

            // Add marker for sensor location
            L.marker([latitude, longitude])
                .addTo(this.map)
                .bindPopup(`<strong>${this.project.title}</strong><br>${this.project.location.address}`)
                .openPopup();
        },
        getChartUrl(channelId, field) {
            if (!channelId) return '';

            return `https://thingspeak.com/channels/${channelId}/charts/${field}?width=auto&height=auto&days=${this.days}&dynamic=true&type=spline`;
        },
        updateCharts() {
            // Force refresh of chart iframes
            const chartIframes = document.querySelectorAll('.thingspeak-chart iframe');
            chartIframes.forEach(iframe => {
                const src = iframe.src;
                iframe.src = src;
            });

            this.checkSensorStatus();
        },
        async checkSensorStatus() {
            if (!this.selectedChannelId) return;

            try {
                const response = await axios.get(`https://api.thingspeak.com/channels/${this.selectedChannelId}/fields/1/last_data_age.txt`);
                const lastPingSeconds = parseInt(response.data);

                this.isOnline = lastPingSeconds < 1000 && lastPingSeconds > 0;
                this.lastPing = this.isOnline ? `T+ ${lastPingSeconds}s` : 'Sensor Offline';
            } catch (error) {
                console.error("Error checking sensor status:", error);
                this.isOnline = false;
                this.lastPing = 'Status Unavailable';
            }
        }
    }
};
</script>
<style scoped>
select#sensor-select.form-control {
    padding: 12px 20px;
}

/* Breadcrumb styles */
.breadcrumb-area {
    background: linear-gradient(to right, #2a5298, #1e3c72);
    color: white;
    padding: 60px 0;
    position: relative;
}

.overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
}

.breadcrumb-title {
    position: relative;
    z-index: 2;
}

.breadcrumb-title h1 {
    font-size: 2.5rem;
    font-weight: bold;
    margin-bottom: 15px;
}

.breadcrumb-meta {
    background: #f4f4f4;
    padding: 15px 0;
}

.breadcrumb {
    margin-bottom: 0;
    padding: 0;
}

/* Section styles */
.section-padding {
    padding: 80px 0;
}

.project-details-section {
    background: #f9f9f9;
}

/* Main content styles */
.project-content {
    background: white;
    padding: 30px;
    border-radius: 8px;
    box-shadow: 0 3px 15px rgba(0, 0, 0, 0.08);
}

.project-content h3 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #2a5298;
    margin-bottom: 20px;
}

.project-content p {
    line-height: 1.7;
    color: #555;
}

.main-image img {
    width: 100%;
    border-radius: 8px;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
}

/* Sidebar and Details Box styles */
.project-sidebar {
    background: white;
    padding: 30px;
    border-radius: 8px;
    box-shadow: 0 3px 15px rgba(0, 0, 0, 0.08);
}

.sidebar-content {
    margin-bottom: 30px;
}

.project-location-box,
.sensor-vitals-box,
.project-info-box {
    margin-bottom: 20px;
}

.project-info-box h4,
.project-location-box h4,
.sensor-vitals-box h4 {
    font-size: 1.2rem;
    font-weight: 600;
    color: #afc9f6;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 1px solid #eee;
}

/* List styles */
.sensor-vitals-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.sensor-vitals-list li {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    font-size: 15px;
    padding-bottom: 8px;
    border-bottom: 1px dashed #eee;
}

.sensor-vitals-list li:last-child {
    border-bottom: none;
}

/* Location styles */
.location-address {
    font-size: 14px;
    color: #666;
}

.location-address i {
    color: #2a5298;
    margin-right: 5px;
}

/* Status indicator styles */
.status-indicator {
    display: flex;
    align-items: center;
    padding: 10px 15px;
    border-radius: 5px;
}

.status-indicator.online {
    background-color: #e6f7e6;
}

.status-indicator.offline {
    background-color: #ffebee;
}

.status-dot {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    margin-right: 10px;
}

.online .status-dot {
    background-color: #28a745;
}

.offline .status-dot {
    background-color: #dc3545;
}

.online .status-text {
    color: #28a745;
    font-weight: 600;
}

.offline .status-text {
    color: #dc3545;
    font-weight: 600;
}

.last-ping {
    color: #777;
    font-size: 13px;
}

/* Chart styles */
.chart-container {
    background: white;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.chart-container h4 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 15px;
}

.thingspeak-chart {
    width: 100%;
    overflow: hidden;
    border-radius: 4px;
}

.thingspeak-chart iframe {
    border: none;
}

/* Form styles */
.form-control {
    height: 45px;
    border-radius: 4px;
    border: 1px solid #ddd;
    box-shadow: none;
}

/* Button styles */
.btn-primary {
    background-color: #2a5298;
    border-color: #2a5298;
}

.btn-primary:hover {
    background-color: #1e3c72;
    border-color: #1e3c72;
}

.btn-outline-primary {
    color: #2a5298;
    border-color: #2a5298;
}

.btn-outline-primary:hover {
    background-color: #2a5298;
    color: white;
}

button.btn.btn-primary {
    height: 45px;
}

/* Loading container */
.loading-container {
    min-height: 400px;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Architecture image */
.architecture-image {
    text-align: center;
    margin-top: 20px;
}

.architecture-image img {
    max-width: 100%;
    border-radius: 8px;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
}

/* Responsive styles */
@media (max-width: 991px) {
    .breadcrumb-title h1 {
        font-size: 2rem;
    }

    .project-sidebar {
        margin-top: 30px;
    }
}

@media (max-width: 767px) {
    .section-padding {
        padding: 50px 0;
    }

    .project-content,
    .project-sidebar {
        padding: 20px;
    }

    .breadcrumb-title h1 {
        font-size: 1.8rem;
    }

    .project-content h3 {
        font-size: 1.3rem;
    }
}

/* General styles for info lists if needed (or adjust existing) */
.project-info-box ul,
.sensor-vitals-box ul {
    list-style: none;
    padding: 0;
    margin: 0;
}

.project-info-box li,
.sensor-vitals-box li {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 8px 0;
    border-bottom: 1px dashed #eee;
    font-size: 15px;
}

.project-info-box li:last-child,
.sensor-vitals-box li:last-child {
    border-bottom: none;
}

.project-info-box li strong,
.sensor-vitals-box li strong {
    color: #d4d1d1;
    margin-right: 10px;
    flex-shrink: 0;
}

.project-info-box li span,
.sensor-vitals-box li .sensor-value {
    color: #a5a3a3;
    text-align: right;
    flex-grow: 1;
}
</style>